import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Button, Modal, Form, Spinner, Alert } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faSchool, faPlus } from '@fortawesome/free-solid-svg-icons';
import SchoolService from '../../services/school.service';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

const MunicipalSchools = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    cnpj: '',
    phone: ''
  });

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    fetchSchools();
  }, []);

  const fetchSchools = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await SchoolService.getAll();
      // Dados já vêm filtrados do backend por município
      setSchools(data.results || []);
    } catch (err) {
      setError('Erro ao carregar escolas. Tente novamente.');
      console.error('Error fetching schools:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (school) => {
    setSelectedSchool(school);
    setFormData({
      name: school.name,
      cnpj: school.cnpj,
      phone: school.phone || ''
    });
    setShowEditModal(true);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setSelectedSchool(null);
    setFormData({
      name: '',
      cnpj: '',
      phone: ''
    });
  };

  const handleOpenCreateModal = () => {
    setFormData({
      name: '',
      cnpj: '',
      phone: ''
    });
    setShowCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setFormData({
      name: '',
      cnpj: '',
      phone: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    try {
      const payload = {
        name: formData.name,
        cnpj: formData.cnpj,
        phone: formData.phone
      };
      await SchoolService.partialUpdate(selectedSchool.id, payload);
      await fetchSchools();
      handleCloseModal();
      showToast('Escola atualizada com sucesso!', 'success');
    } catch (err) {
      console.error('Error updating school:', err);
      showToast('Erro ao atualizar escola. Tente novamente.', 'danger');
    }
  };

  const handleCreate = async () => {
    try {
      const activeMunicipality = user?.municipality_users?.find(mu => mu.active);
      if (!activeMunicipality) {
        showToast('Nenhum município ativo encontrado.', 'warning');
        return;
      }

      const payload = {
        name: formData.name,
        cnpj: formData.cnpj,
        phone: formData.phone,
        municipality: activeMunicipality.municipality.id
      };
      await SchoolService.create(payload);
      await fetchSchools();
      handleCloseCreateModal();
      showToast('Escola criada com sucesso!', 'success');
    } catch (err) {
      console.error('Error creating school:', err);
      showToast('Erro ao criar escola. Tente novamente.', 'danger');
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <h4>Gestão de Escolas Municipais</h4>
          <p className="mb-0">Gerencie as escolas do seu município</p>
        </div>
      </div>

      <Row>
        <Col xs={12} className="mb-4">
          <Card border="light" className="shadow-sm">
            <Card.Header>
              <Row className="align-items-center">
                <Col>
                  <h5 className="mb-0">
                    <FontAwesomeIcon icon={faSchool} className="me-2" />
                    Lista de Escolas
                  </h5>
                </Col>
                <Col className="text-end">
                  <Button 
                    variant="primary" 
                    size="sm"
                    onClick={handleOpenCreateModal}
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-1" />
                    Nova Escola
                  </Button>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="p-0">
              {loading && (
                <div className="text-center py-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </Spinner>
                </div>
              )}
              {error && (
                <Alert variant="danger" className="m-3">
                  {error}
                </Alert>
              )}
              {!loading && !error && schools.length === 0 && (
                <div className="text-center py-5">
                  <p className="text-muted">Nenhuma escola encontrada.</p>
                </div>
              )}
              {!loading && !error && schools.length > 0 && (
                <Table responsive hover className="align-items-center table-flush mb-0">
                  <thead className="thead-light">
                    <tr>
                      <th>Nome</th>
                      <th>CNPJ</th>
                      <th>Telefone</th>
                      <th>Município</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schools.map((school) => (
                      <tr key={school.id}>
                        <td className="fw-bold">{school.name}</td>
                        <td>{school.cnpj}</td>
                        <td>{school.phone || '-'}</td>
                        <td>{school.municipality_name}</td>
                        <td>
                          <Button
                            variant="info"
                            size="sm"
                            onClick={() => handleEdit(school)}
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modal de Edição */}
      <Modal show={showEditModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Editar Escola</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Nome da Escola</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Nome completo da escola"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>CNPJ</Form.Label>
              <Form.Control
                type="text"
                name="cnpj"
                value={formData.cnpj}
                onChange={handleInputChange}
                placeholder="00.000.000/0000-00"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Telefone</Form.Label>
              <Form.Control
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="(00) 00000-0000"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Salvar Alterações
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Criação */}
      <Modal show={showCreateModal} onHide={handleCloseCreateModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Nova Escola</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Nome da Escola</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Nome completo da escola"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>CNPJ</Form.Label>
              <Form.Control
                type="text"
                name="cnpj"
                value={formData.cnpj}
                onChange={handleInputChange}
                placeholder="00.000.000/0000-00"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Telefone</Form.Label>
              <Form.Control
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="(00) 00000-0000"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseCreateModal}>
            Cancelar
          </Button>
          <Button 
            variant="primary" 
            onClick={handleCreate}
            disabled={!formData.name || !formData.cnpj}
          >
            Criar Escola
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MunicipalSchools;
